import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/dashboard/Sidebar';

const TOTAL_STEPS = 4;
const STEP_LABELS = ['Welcome', 'Review Codes', 'Configure Projects', 'Complete'];

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Step 2 state
  const [masterCodes, setMasterCodes] = useState([]);
  const [selectedCodes, setSelectedCodes] = useState({});
  const [activeFilter, setActiveFilter] = useState('all');
  const [createResult, setCreateResult] = useState(null);
  const [createdCodes, setCreatedCodes] = useState([]);

  // Step 3 state
  const [projects, setProjects] = useState([]);
  const [configuringProject, setConfiguringProject] = useState(null);
  const [projectCodeSelection, setProjectCodeSelection] = useState({});
  const [configuring, setConfiguring] = useState(false);
  const [applyingAll, setApplyingAll] = useState(false);

  // Step 4 state
  const [finalSummary, setFinalSummary] = useState(null);

  // Cache state
  const [fromCache, setFromCache] = useState(false);

  const clearError = () => setError(null);

  // ── Step 1 → 2: Generate master codes ──────────────────────────────────────
  const generateMasterCodes = async (forceRegenerate = false) => {
    setError(null);
    setLoading(true);
    try {
      const res = await axios.post('/api/mapping/wizard/generate-master-codes', {
        force_regenerate: forceRegenerate
      });
      const codes = res.data.suggestions;
      setMasterCodes(codes);
      setFromCache(!!res.data.from_cache);
      const initial = {};
      codes.forEach((_, i) => { initial[i] = true; });
      setSelectedCodes(initial);
      setActiveFilter('all');
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate cost codes. Check that QuickBooks is connected.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2 → 3: Create company codes in Procore ────────────────────────────
  const createCompanyCodes = async () => {
    const approved = masterCodes.filter((_, i) => selectedCodes[i]);
    if (approved.length === 0) {
      setError('Select at least one cost code to create.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await axios.post('/api/mapping/wizard/create-company-codes', { approvedCodes: approved });
      setCreateResult(res.data);
      setCreatedCodes(res.data.createdCodes || []);
      await loadProjectsStatus();
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create company codes in Procore.');
    } finally {
      setLoading(false);
    }
  };

  const loadProjectsStatus = async () => {
    const res = await axios.get('/api/mapping/wizard/projects-status');
    setProjects(res.data.projects || []);
  };

  // ── Configure individual project ───────────────────────────────────────────
  const openProjectConfig = (project) => {
    setConfiguringProject(project);
    const init = {};
    masterCodes.filter(c => !c.is_division).forEach(c => { init[c.code] = true; });
    setProjectCodeSelection(init);
  };

  const applyToProject = async () => {
    if (!configuringProject) return;
    setConfiguring(true);
    try {
      const selectedCodes = createdCodes
        .filter(c => projectCodeSelection[c.code])
        .map(c => ({ code: c.code, name: c.name, parent_id: c.parent_id }));
      await axios.post('/api/mapping/wizard/configure-project', {
        projectId: configuringProject.id,
        selectedCodes
      });
      setConfiguringProject(null);
      await loadProjectsStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to configure project.');
    } finally {
      setConfiguring(false);
    }
  };

  const applyToAllProjects = async () => {
    setApplyingAll(true);
    setError(null);
    try {
      const selectedCodes = createdCodes.map(c => ({ code: c.code, name: c.name, parent_id: c.parent_id }));
      const res = await axios.post('/api/mapping/wizard/apply-to-all-projects', { selectedCodes });
      setFinalSummary({ ...createResult, totalApplied: res.data.totalApplied, projectCount: res.data.projectCount });
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to apply codes to all projects.');
    } finally {
      setApplyingAll(false);
    }
  };

  const finishSetup = () => {
    setFinalSummary({ ...createResult });
    setStep(4);
  };

  // ── Derived values ─────────────────────────────────────────────────────────
  const divisions = masterCodes.filter(c => c.is_division);
  const filteredCodes = activeFilter === 'all'
    ? masterCodes
    : masterCodes.filter(c => c.code === activeFilter || c.parent_code === activeFilter);
  const selectedCount = Object.values(selectedCodes).filter(Boolean).length;

  const toggleAllCodes = (val) => {
    const next = {};
    masterCodes.forEach((_, i) => { next[i] = val; });
    setSelectedCodes(next);
  };

  return (
    <div className="app-layout" style={{ display: 'flex' }}>
      <Sidebar />
      <main className="app-main" style={{ flex: 1, minWidth: 0 }}>

        {/* Page header */}
        <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h2 className="page-title">Setup Wizard</h2>
            <p className="page-sub">Build your CSI MasterFormat cost code structure and configure projects</p>
          </div>
        </div>

        <ProgressBar current={step} />

        {error && (
          <div className="alert alert-error" style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <span style={{ fontSize: 13 }}>{error}</span>
            <button onClick={clearError} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 16, flexShrink: 0, lineHeight: 1 }}>✕</button>
          </div>
        )}

        {/* ── Step 1: Welcome ────────────────────────────────────────────── */}
        {step === 1 && (
          <div className="card card-glass" style={{ maxWidth: 560 }}>
            <div className="card-header">
              <span className="card-title">Welcome to SyncBridge Setup</span>
            </div>
            <div className="card-body">
              <p style={{ color: 'var(--muted2)', fontSize: 14, marginBottom: 20, lineHeight: 1.65 }}>
                This wizard builds your cost code structure in 3 phases. It only needs to run once — after setup, all syncs will use these codes automatically.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
                {[
                  { num: '1', title: 'Generate Master Codes', desc: 'AI analyzes your QuickBooks chart of accounts and creates a proper CSI MasterFormat hierarchy with divisions and sub codes.' },
                  { num: '2', title: 'Review & Create in Procore', desc: 'Review the suggested code structure, exclude anything irrelevant, then push to your Procore company cost code library.' },
                  { num: '3', title: 'Configure Projects', desc: 'Choose which cost codes apply to each project, or apply all codes to every project at once.' }
                ].map(item => (
                  <div key={item.num} style={{ display: 'flex', gap: 14, padding: '14px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.07)' }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                      background: 'rgba(99,179,237,0.12)', border: '1.5px solid rgba(99,179,237,0.3)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 700, color: '#63b3ed'
                    }}>
                      {item.num}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 3 }}>{item.title}</div>
                      <div style={{ fontSize: 12.5, color: 'var(--muted2)', lineHeight: 1.55 }}>{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="btn btn-primary" onClick={() => generateMasterCodes(false)} disabled={loading}>
                  {loading
                    ? <><div className="spinner" style={{ width: 13, height: 13 }} /> Analyzing QuickBooks accounts...</>
                    : '✦ Generate Master Cost Codes'}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => generateMasterCodes(true)} disabled={loading}
                  title="Discard saved results and re-run AI analysis">
                  ↺ Regenerate
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 2: Review & Approve Master Codes ──────────────────────── */}
        {step === 2 && (
          <div className="card card-glass">
            {fromCache && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                padding: '10px 20px', background: 'rgba(234,179,8,0.07)',
                borderBottom: '1px solid rgba(234,179,8,0.18)', fontSize: 13, color: 'rgba(234,179,8,0.9)'
              }}>
                <span>Showing saved results from last generation.</span>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => { setStep(1); }}
                  style={{ color: 'rgba(234,179,8,0.9)', fontSize: 12 }}
                >
                  ↺ Regenerate fresh codes
                </button>
              </div>
            )}
            <div className="card-header" style={{ flexWrap: 'wrap', gap: 10 }}>
              <span className="card-title">Review Master Cost Codes</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: 'var(--muted2)' }}>
                  {selectedCount} of {masterCodes.length} selected
                </span>
                <button className="btn btn-ghost btn-sm" onClick={() => toggleAllCodes(true)}>Select All</button>
                <button className="btn btn-ghost btn-sm" onClick={() => toggleAllCodes(false)}>Deselect All</button>
              </div>
            </div>
            <div className="card-body">

              {/* Division filter chips */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
                <button
                  className={`btn btn-sm ${activeFilter === 'all' ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setActiveFilter('all')}
                >
                  All ({masterCodes.length})
                </button>
                {divisions.map(div => (
                  <button
                    key={div.code}
                    className={`btn btn-sm ${activeFilter === div.code ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => setActiveFilter(div.code)}
                  >
                    {div.code}
                  </button>
                ))}
              </div>

              {/* Cost code tree */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 480, overflowY: 'auto' }}>
                {filteredCodes.map((code) => {
                  const globalIdx = masterCodes.indexOf(code);
                  const isDiv = code.is_division;
                  const isSelected = !!selectedCodes[globalIdx];
                  return (
                    <div
                      key={code.code}
                      onClick={() => setSelectedCodes(prev => ({ ...prev, [globalIdx]: !prev[globalIdx] }))}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: isDiv ? '10px 14px' : '7px 14px 7px 36px',
                        background: isDiv ? 'rgba(99,179,237,0.06)' : isSelected ? 'rgba(255,255,255,0.025)' : 'transparent',
                        borderRadius: 8,
                        border: isDiv ? '1px solid rgba(99,179,237,0.14)' : '1px solid transparent',
                        cursor: 'pointer',
                        opacity: isSelected ? 1 : 0.38,
                        transition: 'opacity 0.12s'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={e => { e.stopPropagation(); setSelectedCodes(prev => ({ ...prev, [globalIdx]: e.target.checked })); }}
                        onClick={e => e.stopPropagation()}
                        style={{ cursor: 'pointer', flexShrink: 0 }}
                      />
                      <span style={{
                        fontFamily: 'monospace', fontSize: 12, fontWeight: isDiv ? 700 : 500,
                        color: isDiv ? '#63b3ed' : 'var(--muted2)', minWidth: 58, flexShrink: 0
                      }}>
                        {code.code}
                      </span>
                      <span style={{ flex: 1, fontSize: 13, fontWeight: isDiv ? 600 : 400, color: 'var(--text)' }}>
                        {code.name}
                      </span>
                      {code.qb_account_name && (
                        <span style={{ fontSize: 11.5, color: 'var(--muted2)', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          → {code.qb_account_name}
                        </span>
                      )}
                      {isDiv && <span className="badge" style={{ fontSize: 10, flexShrink: 0 }}>Division</span>}
                    </div>
                  );
                })}
              </div>

              <div style={{ display: 'flex', gap: 10, alignItems: 'center', paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.07)', marginTop: 16 }}>
                <button className="btn btn-primary" onClick={createCompanyCodes} disabled={loading || selectedCount === 0}>
                  {loading
                    ? <><div className="spinner" style={{ width: 13, height: 13 }} /> Creating in Procore...</>
                    : `Create ${selectedCount} Cost Code${selectedCount !== 1 ? 's' : ''} in Procore`}
                </button>
                <button className="btn btn-ghost" onClick={() => setStep(1)}>Back</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 3: Project Configuration ──────────────────────────────── */}
        {step === 3 && (
          <div className="card card-glass">
            <div className="card-header">
              <span className="card-title">Configure Projects</span>
              <button className="btn btn-primary btn-sm" onClick={applyToAllProjects} disabled={applyingAll}>
                {applyingAll
                  ? <><div className="spinner" style={{ width: 11, height: 11 }} /> Applying...</>
                  : 'Apply All Codes to Every Project'}
              </button>
            </div>
            <div className="card-body">
              {createResult && (
                <div style={{
                  background: 'rgba(110,255,159,0.07)', border: '1px solid rgba(110,255,159,0.2)',
                  borderRadius: 8, padding: '10px 14px', marginBottom: 16,
                  fontSize: 13, color: 'var(--success)'
                }}>
                  ✓ Created {createResult.created} cost codes in Procore company library.
                  {createResult.skipped > 0 && ` ${createResult.skipped} already existed.`}
                </div>
              )}

              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Project Name</th>
                      <th>Project #</th>
                      <th>Status</th>
                      <th>Cost Codes</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map(project => (
                      <tr key={project.id}>
                        <td style={{ fontWeight: 500 }}>{project.name}</td>
                        <td style={{ color: 'var(--muted2)', fontSize: 13 }}>{project.project_number || '—'}</td>
                        <td>
                          <span className={`badge badge-${project.status === 'Active' ? 'success' : 'muted'}`}>
                            {project.status || 'Unknown'}
                          </span>
                        </td>
                        <td style={{ fontSize: 13 }}>
                          {project.cost_code_count}
                          {project.needs_setup && (
                            <span className="badge badge-warning" style={{ marginLeft: 8, fontSize: 11 }}>Needs Setup</span>
                          )}
                        </td>
                        <td>
                          <button className="btn btn-ghost btn-sm" onClick={() => openProjectConfig(project)}>
                            Configure
                          </button>
                        </td>
                      </tr>
                    ))}
                    {projects.length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'center', color: 'var(--muted2)', padding: 28 }}>
                          No projects found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div style={{ display: 'flex', gap: 10, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.07)', marginTop: 8 }}>
                <button className="btn btn-primary" onClick={finishSetup}>Finish Setup</button>
                <button className="btn btn-ghost" onClick={() => setStep(2)}>Back</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 4: Complete ────────────────────────────────────────────── */}
        {step === 4 && (
          <div className="card card-glass" style={{ maxWidth: 520 }}>
            <div className="card-body" style={{ padding: 44, textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
              <h3 style={{ marginBottom: 8, color: 'var(--text)' }}>Setup Complete</h3>
              <p style={{ color: 'var(--muted2)', marginBottom: 28, fontSize: 14, lineHeight: 1.6 }}>
                Your CSI MasterFormat cost code structure is live in Procore and mapped to your QuickBooks accounts.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 28, textAlign: 'left' }}>
                {[
                  { label: 'Codes Created', value: finalSummary?.created ?? 0 },
                  { label: 'Already Existed', value: finalSummary?.skipped ?? 0 },
                  { label: 'Projects Updated', value: finalSummary?.projectCount ?? projects.length },
                  { label: 'QB Mappings Saved', value: finalSummary?.created ?? 0 }
                ].map(s => (
                  <div key={s.label} className="stat-card">
                    <div className="stat-label">{s.label}</div>
                    <div className="stat-value">{s.value}</div>
                  </div>
                ))}
              </div>
              {(finalSummary?.errors?.length > 0) && (
                <div className="alert alert-error" style={{ textAlign: 'left', marginBottom: 20 }}>
                  {finalSummary.errors.map((e, i) => (
                    <div key={i} style={{ fontSize: 12 }}><strong>{e.code}:</strong> {e.error}</div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button className="btn btn-primary" onClick={() => navigate('/')}>Go to Dashboard</button>
                <button className="btn btn-ghost" onClick={() => navigate('/mapping')}>View Mappings</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Project Configure Modal ─────────────────────────────────────── */}
        {configuringProject && (
          <div className="modal-overlay">
            <div className="modal" style={{ maxWidth: 560, maxHeight: '82vh', display: 'flex', flexDirection: 'column' }}>
              <h3 className="modal-title">Configure: {configuringProject.name}</h3>
              <p className="modal-desc">Select cost codes that apply to this project's scope of work.</p>
              <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4, marginBottom: 4 }}>
                {divisions.map(div => {
                  const subCodes = masterCodes.filter(c => !c.is_division && c.parent_code === div.code);
                  if (subCodes.length === 0) return null;
                  return (
                    <div key={div.code} style={{ marginBottom: 14 }}>
                      <div style={{
                        fontSize: 11, fontWeight: 700, color: '#63b3ed',
                        textTransform: 'uppercase', letterSpacing: '0.06em',
                        marginBottom: 6, padding: '4px 0',
                        borderBottom: '1px solid rgba(99,179,237,0.15)'
                      }}>
                        {div.code} — {div.name}
                      </div>
                      {subCodes.map(sub => (
                        <label key={sub.code} style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '7px 8px', cursor: 'pointer', borderRadius: 6,
                          background: projectCodeSelection[sub.code] ? 'rgba(255,255,255,0.04)' : 'transparent'
                        }}>
                          <input
                            type="checkbox"
                            checked={!!projectCodeSelection[sub.code]}
                            onChange={e => setProjectCodeSelection(prev => ({ ...prev, [sub.code]: e.target.checked }))}
                            style={{ cursor: 'pointer', flexShrink: 0 }}
                          />
                          <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--muted2)', minWidth: 52 }}>{sub.code}</span>
                          <span style={{ fontSize: 13, color: 'var(--text)' }}>{sub.name}</span>
                        </label>
                      ))}
                    </div>
                  );
                })}
              </div>
              <div className="modal-actions">
                <button className="btn btn-ghost" onClick={() => setConfiguringProject(null)} disabled={configuring}>Cancel</button>
                <button className="btn btn-primary" onClick={applyToProject} disabled={configuring}>
                  {configuring ? <><div className="spinner" style={{ width: 12, height: 12 }} /> Applying...</> : 'Apply to Project'}
                </button>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}

function ProgressBar({ current }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 28 }}>
      {STEP_LABELS.map((label, i) => {
        const num = i + 1;
        const active = num === current;
        const done = num < current;
        return (
          <React.Fragment key={i}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700,
                background: done ? 'var(--success)' : active ? '#63b3ed' : 'rgba(255,255,255,0.05)',
                border: `2px solid ${done ? 'var(--success)' : active ? '#63b3ed' : 'rgba(255,255,255,0.14)'}`,
                color: done || active ? '#fff' : 'var(--muted2)'
              }}>
                {done ? '✓' : num}
              </div>
              <span style={{
                fontSize: 13, fontWeight: active ? 600 : 400, whiteSpace: 'nowrap',
                color: active ? 'var(--text)' : done ? 'var(--success)' : 'var(--muted2)'
              }}>
                {label}
              </span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div style={{
                flex: 1, height: 1, margin: '0 10px', minWidth: 16,
                background: done ? 'var(--success)' : 'rgba(255,255,255,0.09)'
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
